import { zodResolver } from "@hookform/resolvers/zod";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Button, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { omit } from "lodash-es";
import type { FC } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSupabaseCallback } from "../../../../../../server/supabase/hooks";
import type { MomentFormMoment } from "../../../../../moment/types/moment-form-moment";
import { SupabasePollService } from "../../../../../poll/server/supabase-poll-service";
import type { EditorModalState } from "../../../lexical-context";
import { INSERT_POLL_COMMAND, MODIFY_POLL_COMMAND } from "../../../types/commands";
import { usePollButtonCommands } from "../../toolbar/poll/hooks/use-poll-button-commands";
import type { PollPresets } from "../hooks/use-poll-default-values";
import { PollResultsToggle } from "../poll-results-toggle";
import { type PollWrapperSchema, pollWrapperSchema } from "../types";
import { PollFormCustom } from "./poll-form-custom";
import { PollFormPreset } from "./poll-form-preset";
import { PollFormSelectQuestionType } from "./poll-form-select-question-type";

export const PollForm: FC<{
  action: Extract<EditorModalState, { type: "poll" }>["data"];
  defaultValues: PollWrapperSchema;
  presets: PollPresets;
  handleClose: () => void;
}> = ({ action, defaultValues, presets, handleClose }) => {
  const { t } = useTranslation();

  usePollButtonCommands();
  const [editor] = useLexicalComposerContext();

  const { setValue, getValues } = useFormContext<Pick<MomentFormMoment, "poll_ids">>();

  const form = useForm({
    defaultValues,
    resolver: zodResolver(pollWrapperSchema),
  });

  const { handleSubmit, control, getValues: getInnerValues } = form;

  useWatch({ control, name: "question_type" });
  const question_type = getInnerValues("question_type");

  const onSubmit = useSupabaseCallback(
    async ({ supabase }, { poll: values }: PollWrapperSchema) => {
      const { error } = await new SupabasePollService(supabase).upsertPoll(
        omit(values, "answer_type"),
      );
      if (error) {
        console.log("Error creating poll", error);
        toast.error(t("Error creating poll"));
        return;
      }

      setValue("poll_ids", [...new Set(getValues("poll_ids") || []).add(values.id)]);

      action.mode === "add"
        ? editor.dispatchCommand(INSERT_POLL_COMMAND, {
            poll_id: values.id,
            answer_type: values.answer_type,
          })
        : editor.dispatchCommand(MODIFY_POLL_COMMAND, {
            nodeKey: action.key,
            poll_id: values.id,
            answer_type: values.answer_type,
          });

      handleClose();
    },
    [t, handleClose, editor, setValue, getValues, action],
  );

  return (
    <FormProvider {...form}>
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>{t("Create poll")}</DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={2} sx={{ pt: 1 }}>
            <PollFormSelectQuestionType presets={presets} />
            {question_type.type === "preset" && <PollFormPreset />}
            {question_type.type === "custom" && <PollFormCustom presets={presets} />}
            <PollResultsToggle />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} data-analytics-id="cancel-poll-insert">
            {t("Cancel")}
          </Button>
          <Button type="submit" data-analytics-id="insert-poll">
            {action.mode === "add" ? t("Add poll to Communication") : t("Update poll")}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
};
